import { getManagerNotes } from '../actions/managerNotesActions';
import {
    getOptionalSurvey,
    resetSubDomainCursor,
    SkillClusterType,
    updateUserCompleted,
} from '../actions/surveyActions';
import Button from '../components/Button';
import GridWrapper from '../components/GridWrapper';
import { Domain } from '../model/models';
import { State } from '../redux-state';
import { containerStyles } from '../styles';
import breakpoints from '../styles/breakpoints';
import colors from '../styles/colors';
import fonts from '../styles/fonts';
import { globalColor } from '../utils/globals';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import getContrastColor from '../utils/contrastColor';

const WelcomeSurveyContainer = styled.div`
    ${containerStyles.fullPage};
    background: #fcfcfc;
    ${breakpoints.onlyMobile} {
        width: 100%;
    }
`;

const BottomSectionContainer = styled.div`
    display: flex;
    min-height: 768px;
    width: 1024px;
    margin: 0 auto;
    ${breakpoints.onlyMobile} {
        width: 100%;
        height: 100%;
        flex-direction: column;
    }
`;

const LeftSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    padding: 20px;
    width: 44%;
    margin-top: 30px;
    ${breakpoints.onlyMobile} {
        width: 100%;
        padding: 0 0 0 0;
    }
`;

const RightGrid = styled.div`
    padding: 20px 0;
    display: flex;
    align-items: flex-start;
    width: 56%;
    flex-direction: column;
    ${breakpoints.onlyMobile} {
        width: 100%;
        padding: 12px 0;
    }
`;

const Title = styled.div`
    ${fonts.subtitle};
    font-size: 32px;
    width: 100%;
    color: ${() => globalColor.titleColor};
    font-weight: bold;
    text-transform: uppercase;
    margin-bottom: 32px;
`;

const Greetings = styled.div<{ backgroundColor?: string }>`
    ${fonts.content};
    font-size: 15px;
    color: ${colors.textColor1};
    margin-bottom: 50px;
    ${breakpoints.onlyMobile} {
        margin-bottom: 15px;
    }

    .optional-link-trigger {
        text-decoration: underline;
        cursor: pointer;
        font-weight: bold;
        background-color: ${(props) => props.backgroundColor || '#ffffff'};
        color: ${(props) =>
            getContrastColor(props.backgroundColor || '#ffffff')};
        padding: 2px 8px;
        border-radius: 4px;
        line-height: 1.5;
    }

    .warning-text {
        color: red;
    }
`;

const LogoImageContainer = styled.div`
     {
        display: flex;
        justify-content: flex-start;
    }
`;

const ClusterCount = styled.span`
    font-size: 18px;
    font-weight: bold;
    padding-right: 6px;
`;

const CompletedText = styled.span`
    font-size: 16px;
`;

const HeadingContainer = styled.div`
    color: ${colors.textColor1};
    margin-bottom: 12px;
    ${breakpoints.onlyMobile} {
        width: 100%;
    }
`;

const LogoImage = styled.img`
    max-height: 120px;
    margin-left: 0%;
    object-fit: contain;
    width: 14.3%;

    ${breakpoints.lg} {
        width: 14.3%;
        margin-left: 0%;
    }

    ${breakpoints.onlyMobile} {
        max-width: 25%;
        width: 100%;
    }

    ${breakpoints.md} {
        width: 14.3%;
        margin-left: 0%;
    }
`;

const StyledNavLink = styled(NavLink)`
    display: flex;
    flex-direction: column;
    text-decoration: none;
    width: 100%;

    span {
        color: ${colors.textColor1};
    }

    &:hover span,
    &:visited span,
    &:active span {
        color: ${colors.textColor1};
    }
`;

const FullWidthButton = styled(Button)`
    font-size: 19px;
    &:focus-visible {
        outline: none;
    }
    &.optional-survey-button {
       
    }

    &.submit-survey-button {
        width: 274px;
    }

    &.start-survey-button {
        width: 67%;
    }

     ${breakpoints.onlyMobile} {    
     font-size:16px;}


  
}
`;

const ButtonContainer = styled.div<{ backgroundColor?: string }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: flex-start;

    ${breakpoints.onlyMobile} {
        align-items: center;
        justify-content: center;
        width: calc(100vw - 44px) !important;
        background-color: ${(props) => props.backgroundColor || '#fbfbfb'};
        padding: 12px 0;
        position: fixed;
        bottom: 0;
        z-index: 9;
        display: flex;
    }
`;

const TabContainer = styled.div`
    display: flex;
    margin-bottom: 12px;
    ${breakpoints.onlyMobile} {
        width: 100%;
    }
`;

const TabButton = styled.div<{
    selected: boolean;
    selectedBackgroundColor?: string;
    isFirst?: boolean;
    isLast?: boolean;
}>`
    padding: 9px 20px;
    cursor: pointer;
    font-weight: bold;
    background-color: ${({ selected, selectedBackgroundColor }) =>
        selected
            ? selectedBackgroundColor || globalColor.backgroundColor2
            : '#fcfcfc'};
    color: ${({ selected, selectedBackgroundColor }) =>
        selected
            ? '#fcfcfc'
            : selectedBackgroundColor || globalColor.backgroundColor2};
    transition: background-color 0.2s, color 0.2s;
    border: 1px solid
        ${({ selectedBackgroundColor }) =>
            selectedBackgroundColor || globalColor.backgroundColor2};

    &:hover {
        background-color: ${({ selected, selectedBackgroundColor }) =>
            selected
                ? selectedBackgroundColor || globalColor.backgroundColor2
                : '#e0e0e0'};
    }
    display: flex;
    align-items: center;
    font-size: 18px;

    // Add styles for the first and last buttons
    ${({ isFirst }) =>
        isFirst &&
        `
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
    `}

    ${({ isLast }) =>
        isLast &&
        `
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
    `}

    ${breakpoints.onlyMobile} {
        font-size: 13px;
        padding: 8px 6px;
        width: 50%;
    }
`;

const getTitleAndSubtitle = (
    mandatoryDone: boolean,
    anyMandatoryQuestionDone: boolean,
    anyOptionalSkillClusterOpted: boolean,
    optionalDone: boolean,
    anyOptionalSkillClusterSelected: boolean,
    t: any,
): { title: string; subTitle: string } => {
    let title = '';
    let subTitle = '';

    if (mandatoryDone && optionalDone) {
        title = t('doneTitle');
        subTitle = t('doneGreetingsText');
    } else if (
        mandatoryDone &&
        !optionalDone &&
        !anyOptionalSkillClusterOpted
    ) {
        title = t('mandatoryDoneTitle');
        subTitle = t('mandatoryDoneGreetingsText');
    } else if (mandatoryDone && !optionalDone && anyOptionalSkillClusterOpted) {
        title = t('doneTitle');
        subTitle = t('doneGreetingsText');
    } else if (!mandatoryDone && anyMandatoryQuestionDone) {
        title = t('mandatoryContinueClusterTitle');
        subTitle = t('mandatoryContinueGreetingsText');
    } else {
        title = t('mandatoryClusterTitle');
        subTitle = t('mandatoryGreetingsText');
    }

    return { title, subTitle };
};

// Combine domain completion logic
const hasFilledAllDomains = (domains: Domain[]) => {
    const selectedDomains = domains.filter((d) => d.isSelected !== false);
    return (
        selectedDomains.filter((d) => d.completed).length ===
        selectedDomains.length
    );
};

function useLocationState() {
    const location = useLocation();
    const stateVar = location.state || {};
    let emailAuth = stateVar.emailAuth;
    if (!emailAuth) {
        const storedEmailAuth = sessionStorage.getItem('emailAuth');
        if (storedEmailAuth) {
            emailAuth = storedEmailAuth;
        }
    }
    return {
        emailAuth,
        levels_state: stateVar.levels_state,
    };
}

const MandatorySurveyWelcomeLeft: React.FC = () => {
    const { t } = useTranslation(['SurveyWelcomeMandatory']);
    const { survey_id, uid } = useParams();
    const domains: Domain[] = useSelector(
        (state: State) => state.surveyState.domains,
    );
    const optionalDomains: Domain[] = useSelector(
        (state: State) => state.optionalSurveyState.domains,
    );
    const anyOptionalSkillClusterOpted = useSelector(
        (state: State) =>
            !!state.optionalSurveyState.userInfos.optionalDomainSelected,
    );
    const { emailAuth, levels_state } = useLocationState();
    const feedbackFeatureEnabled = useSelector(
        (state: State) => state.surveyState.feedbackFeatureEnabled || false,
    );
    const managerNotesEnabled = useSelector(
        (state: State) => state.surveyState.managerNotesEnabled || false,
    );
    const summaryFeatureEnabled = useSelector(
        (state: State) => state.surveyState.summaryFeatureEnabled || false,
    );
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const isManagerFlow = sessionStorage.getItem('isManagerFlow') === 'true';
    const managerUid = sessionStorage.getItem('managerUid');

    // Prefetch the manager notes for this reportee while the welcome page is
    // shown, so the data is already in Redux by the time the user reaches the
    // manager-notes page (no stale flash, no empty placeholder).
    React.useEffect(() => {
        if (managerNotesEnabled && isManagerFlow && managerUid) {
            dispatch(getManagerNotes(survey_id, uid, managerUid) as any);
        }
    }, [managerNotesEnabled, isManagerFlow, managerUid, survey_id, uid]);

    const mandatoryDone = hasFilledAllDomains(domains);
    const anyMandatoryQuestionDone = domains.some((domain) =>
        domain.subdomains.some((sd) => sd.level !== null),
    );
    const optionalDone = anyOptionalSkillClusterOpted
        ? hasFilledAllDomains(optionalDomains)
        : false;

    const anyOptionalSkillClusterSelected = !!optionalDomains.filter(
        (d) => d.isSelected,
    ).length;

    const titleWithSubTitle = getTitleAndSubtitle(
        mandatoryDone,
        anyMandatoryQuestionDone,
        anyOptionalSkillClusterOpted,
        optionalDone,
        anyOptionalSkillClusterSelected,
        t,
    );

    const performNext = (success: boolean) => {
        if (success) {
            if (isManagerFlow && managerUid) {
                navigate(
                    managerNotesEnabled
                        ? `/${survey_id}/${uid}/manager-notes`
                        : `/${survey_id}/${managerUid}/reportees`,
                    { state: { emailAuth }, replace: true },
                );
                return;
            }
            navigate(
                `/${survey_id}/${uid}/${
                    summaryFeatureEnabled ? 'summary' : 'whatsnext'
                }`,
                {
                    state: { levels_state, emailAuth },
                    replace: true,
                },
            );
        }
    };

    const getNextPath = (): string => {
        if (isManagerFlow && managerUid) {
            return managerNotesEnabled
                ? `/${survey_id}/${uid}/manager-notes`
                : `/${survey_id}/${managerUid}/reportees`;
        }
        if (feedbackFeatureEnabled) {
            return `/${survey_id}/${uid}/feedback`;
        }
        return `/${survey_id}/${uid}/${
            summaryFeatureEnabled ? 'summary' : 'whatsnext'
        }`;
    };

    const nextPath = getNextPath();

    // Find the first incomplete domain
    const firstIncompleteDomain = domains.find((d) => !d.completed);

    // Check if all mandatory domains are untouched (not completed and no subdomain has a level)
    const allMandatoryDomainsUntouched =
        domains.length > 0 &&
        domains.every((domain) =>
            domain.subdomains.every(
                (sd) => sd.level === null || sd.level === undefined,
            ),
        );

    // Check if any domain has been touched (not completed, but at least one subdomain has a level)
    const anyMandatoryDomainTouched =
        domains.length > 0 &&
        domains.some((domain) =>
            domain.subdomains.some(
                (sd) => sd.level !== null && sd.level !== undefined,
            ),
        );

    const handleStartOrContinueSurvey = () => {
        if (firstIncompleteDomain) {
            navigate(
                `/${survey_id}/${uid}/survey/${firstIncompleteDomain.id}?type=${SkillClusterType.MANDATORY}`,
                { state: { levels_state, emailAuth } },
            );
        }
    };

    const onContinueOptionalSurvey = async () => {
        await dispatch(getOptionalSurvey(survey_id, uid, false));
        navigate(`/${survey_id}/${uid}/optional-survey`, {
            state: { levels_state, emailAuth },
        });
    };

    // Add a ref for the greetings div
    const greetingsRef = React.useRef<HTMLDivElement>(null);

    // Handle click events within the greetings text
    React.useEffect(() => {
        const handleLinkClick = async (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('optional-link-trigger')) {
                e.preventDefault();
                await dispatch(getOptionalSurvey(survey_id, uid, false));
                navigate(`/${survey_id}/${uid}/optional-survey`, {
                    state: { levels_state, emailAuth },
                });
            }
        };

        const greetingsElement = greetingsRef.current;
        if (greetingsElement) {
            greetingsElement.addEventListener('click', handleLinkClick);
        }

        return () => {
            if (greetingsElement) {
                greetingsElement.removeEventListener('click', handleLinkClick);
            }
        };
    }, [survey_id, uid, navigate, levels_state, emailAuth]);

    return (
        <LeftSection>
            <Title
                dangerouslySetInnerHTML={{ __html: titleWithSubTitle.title }}
            />
            <Greetings
                backgroundColor={globalColor.backgroundColor2}
                ref={greetingsRef}
                dangerouslySetInnerHTML={{ __html: titleWithSubTitle.subTitle }}
            />
            {/* Show Start Survey if untouched, Continue Survey if any touched */}
            {firstIncompleteDomain &&
                (allMandatoryDomainsUntouched || anyMandatoryDomainTouched) && (
                    <ButtonContainer>
                        <FullWidthButton
                            buttonType="primary"
                            className="start-survey-button"
                            onClick={handleStartOrContinueSurvey}
                        >
                            {allMandatoryDomainsUntouched
                                ? t('StartSurvey')
                                : t('ContinueSurvey')}
                        </FullWidthButton>
                    </ButtonContainer>
                )}
            {/* ...existing buttons... */}
            {mandatoryDone && !anyOptionalSkillClusterOpted && (
                <StyledNavLink
                    to={nextPath}
                    state={{ levels_state, emailAuth }}
                    replace={true}
                >
                    <ButtonContainer>
                        <FullWidthButton
                            className="submit-survey-button"
                            borderColor={globalColor.primaryButton}
                            textColor={globalColor.primaryButton}
                            buttonType="primary"
                            onClick={(e: any) => {
                                if (!feedbackFeatureEnabled) {
                                    e.preventDefault();
                                    dispatch(
                                        updateUserCompleted(
                                            survey_id,
                                            uid,
                                            performNext,
                                        ),
                                    );
                                }
                            }}
                        >
                            {t('SubmitSurvey')}
                        </FullWidthButton>
                    </ButtonContainer>
                </StyledNavLink>
            )}
            {mandatoryDone && anyOptionalSkillClusterOpted && (
                <StyledNavLink
                    to={nextPath}
                    state={{ levels_state, emailAuth }}
                    replace={true}
                >
                    <ButtonContainer>
                        <FullWidthButton
                            className="submit-survey-button"
                            borderColor={globalColor.primaryButton}
                            textColor={globalColor.primaryButton}
                            buttonType="primary"
                            onClick={(e: any) => {
                                if (!feedbackFeatureEnabled) {
                                    e.preventDefault();
                                    dispatch(
                                        updateUserCompleted(
                                            survey_id,
                                            uid,
                                            performNext,
                                        ),
                                    );
                                }
                            }}
                        >
                            {t('SubmitSurvey')}
                        </FullWidthButton>
                    </ButtonContainer>
                </StyledNavLink>
            )}
        </LeftSection>
    );
};

const sortDomains = (domains: Domain[]) => {
    return domains.sort((a, b) => {
        if (a.completed === b.completed) {
            return 0;
        }
        return a.completed ? 1 : -1; // Sort completed domains to the end
    });
};

const MandatorySurveyWelcomeRight: React.FC = () => {
    const { t } = useTranslation(['SurveyWelcomeMandatory']);
    const savedTab = sessionStorage.getItem('clusterType');
    const initialTab =
        savedTab === SkillClusterType.OPTIONAL.toString()
            ? SkillClusterType.OPTIONAL
            : SkillClusterType.MANDATORY;

    const [selectedTab, setSelectedTab] =
        React.useState<SkillClusterType>(initialTab);

    // Ensure the tab is saved to sessionStorage on component mount
    React.useEffect(() => {
        // Save the current tab to sessionStorage if it doesn't exist
        if (!sessionStorage.getItem('clusterType')) {
            sessionStorage.setItem('clusterType', selectedTab.toString());
        }
    }, [selectedTab]);

    // Update the tab change handler
    const handleTabChange = (tabType: SkillClusterType) => {
        setSelectedTab(tabType);
        sessionStorage.setItem('clusterType', tabType.toString());
    };

    // Get survey ID and user ID from URL parameters
    const { survey_id, uid } = useParams();

    // Get domains from the Redux store
    const mandatoryDomains: Domain[] = useSelector(
        (state: State): Domain[] => state.surveyState.domains,
    );
    const optionalDomains: Domain[] = useSelector(
        (state: State): Domain[] => state.optionalSurveyState.domains,
    );

    const anyOptionalSkillClusterOpted: boolean = useSelector(
        (state: State): boolean =>
            !!state.optionalSurveyState.userInfos.optionalDomainSelected,
    );
    const mandatoryDone = hasFilledAllDomains(mandatoryDomains);
    const optionalDone = anyOptionalSkillClusterOpted
        ? hasFilledAllDomains(optionalDomains)
        : false;
    const selectedOptionalDomains = optionalDomains.filter((d) => d.isSelected);

    // Add a useEffect to handle tab selection when optional domains change
    React.useEffect(() => {
        if (
            selectedOptionalDomains.length === 0 &&
            selectedTab === SkillClusterType.OPTIONAL
        ) {
            setSelectedTab(SkillClusterType.MANDATORY);
            sessionStorage.setItem(
                'clusterType',
                SkillClusterType.MANDATORY.toString(),
            );
        }
    }, [selectedOptionalDomains.length, selectedTab]);

    // Memoize the domains to display based on current state
    const memoizedDomainsToDisplay = React.useMemo(() => {
        let domainsToShow: Domain[] = [];

        if (mandatoryDone && optionalDone) {
            domainsToShow =
                selectedTab === SkillClusterType.MANDATORY
                    ? [...mandatoryDomains]
                    : selectedOptionalDomains;
        } else if (
            mandatoryDone &&
            !optionalDone &&
            !anyOptionalSkillClusterOpted
        ) {
            domainsToShow = [...mandatoryDomains];
        } else if (
            mandatoryDone &&
            !optionalDone &&
            anyOptionalSkillClusterOpted &&
            !selectedOptionalDomains.length
        ) {
            domainsToShow =
                selectedTab === SkillClusterType.MANDATORY
                    ? [...mandatoryDomains]
                    : [...optionalDomains];
        } else if (
            mandatoryDone &&
            !optionalDone &&
            anyOptionalSkillClusterOpted
        ) {
            domainsToShow =
                selectedTab === SkillClusterType.MANDATORY
                    ? [...mandatoryDomains]
                    : selectedOptionalDomains;
        } else {
            domainsToShow = [...mandatoryDomains];
        }

        return sortDomains([...domainsToShow]);
    }, [
        selectedTab,
        mandatoryDone,
        optionalDone,
        anyOptionalSkillClusterOpted,
        mandatoryDomains,
        optionalDomains,
        selectedOptionalDomains,
    ]);

    let levels_state: any;
    let emailAuth: any;
    const location = useLocation();
    const stateVar = location.state;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    if (stateVar) {
        if ('emailAuth' in stateVar) {
            emailAuth = stateVar.emailAuth;
        }
        if ('levels_state' in stateVar) {
            levels_state = stateVar.levels_state;
        }
    }
    if (!emailAuth) {
        const storedEmailAuth = sessionStorage.getItem('emailAuth');
        if (storedEmailAuth) {
            emailAuth = storedEmailAuth;
        }
    }

    const handleClusterNavigation = (id: string) => {
        const type = selectedTab;

        dispatch(resetSubDomainCursor(id, type));
        const navigationTimeout = setTimeout(() => {
            navigate(
                `/${survey_id}/${uid}/survey/${id}?review=1&&type=${type}`,
                {
                    state: { levels_state, emailAuth },
                },
            );
        }, 100);

        return () => clearTimeout(navigationTimeout);
    };

    const domainGridColumns: {
        key: 'serial' | 'title' | 'levelSummary' | 'totalSkills';
        label: string;
        width?: string | number;
        hideOnMobile?: boolean;
        renderCell?: (value: any, row: any) => React.ReactNode;
    }[] = [
        { key: 'serial', label: t('SerialNumber'), width: 50, hideOnMobile: true },
        {
            key: 'title',
            label: t('SkillCluster'),
            width: breakpoints.onlyMobile ? '60%' : 220,
            renderCell: (_value: any, row: any) => {
                if (mandatoryDone || anyOptionalSkillClusterOpted) {
                    return (
                        <span
                            key={_value.id}
                            onClick={() =>
                                handleClusterNavigation(row.domainObj.id)
                            }
                            style={{
                                textDecoration: 'underline',
                                color: `${colors.textColor1}`,
                                cursor: 'pointer',
                            }}
                        >
                            {row.title}
                        </span>
                    );
                }
                return (
                    <span style={{ color: colors.textColor1 }}>
                        {row.title}
                    </span>
                );
            },
        },
        {
            key: 'totalSkills' as const,
            label: t('TotalSkills'),
            width: breakpoints.onlyMobile ? '20%' : 100,
            renderCell: (_value: any, row: any) => (
                <div style={{ textAlign: 'center', width: '100%' }}>
                    {row.domainObj.subdomains.length}
                </div>
            ),
        },
        {
            key: 'levelSummary' as const,
            label: '',
            width: breakpoints.onlyMobile ? '20%' : 70,
        },
    ];

    const completedSkillClusterCount = memoizedDomainsToDisplay.filter(
        (_domain: Domain) => _domain.completed,
    ).length;

    const domainGridData = memoizedDomainsToDisplay.map((domain, idx) => {
        const totalSubdomains = domain.subdomains.length;
        const levelCount = domain.subdomains.filter(
            (sd) => sd.level !== null,
        ).length;

        const baseSpanStyle: React.CSSProperties = {
            color: 'white',
            fontWeight: 600,
            padding: '4px 8px',
            borderRadius: '4px',
            display: 'inline-block',
            minWidth: '40px',
            textAlign: 'center' as const,
            fontSize: breakpoints.onlyMobile ? '12px' : '15px',
        };

        const levelSummary = (
            <span
                style={{
                    ...baseSpanStyle,
                    backgroundColor:
                        levelCount === totalSubdomains ? '#02B3D6' : '#f2a687',
                }}
            >
                {levelCount === totalSubdomains
                    ? t('Done')
                    : `${levelCount}/${totalSubdomains}`}
            </span>
        );

        return {
            serial: idx + 1,
            title: domain.title,
            domainObj: {
                title: domain.title,
                id: domain.id,
                idx: domain.idx,
                preEvaluationLevel: domain.preEvaluationLevel,
                subdomains: domain.subdomains.map((sd) => ({
                    name: sd.title,
                    level: sd.level ?? null,
                    id: sd.id,
                    idx: sd.idx,
                })),
            },
            totalSkills: totalSubdomains, // <-- Add this line
            levelSummary,
        };
    });

    // Update the TabButton components
    return (
        <RightGrid>
            <HeadingContainer>
                {selectedOptionalDomains.length > 0 && (
                    <TabContainer>
                        <TabButton
                            selectedBackgroundColor={
                                globalColor.backgroundColor2
                            }
                            selected={
                                selectedTab === SkillClusterType.MANDATORY
                            }
                            onClick={() =>
                                handleTabChange(SkillClusterType.MANDATORY)
                            }
                            isFirst={true}
                        >
                            {t('MandatorySkillClusters')}
                        </TabButton>
                        <TabButton
                            selectedBackgroundColor={
                                globalColor.backgroundColor2
                            }
                            selected={selectedTab === SkillClusterType.OPTIONAL}
                            onClick={() =>
                                handleTabChange(SkillClusterType.OPTIONAL)
                            }
                            isLast={true}
                        >
                            {t('AdditionalSkillClusters')}
                        </TabButton>
                    </TabContainer>
                )}
                <ClusterCount>
                    {completedSkillClusterCount}/
                    {memoizedDomainsToDisplay?.length}
                </ClusterCount>
                <CompletedText>{t('SkillClustersCompleted')}</CompletedText>
            </HeadingContainer>
            <GridWrapper columns={domainGridColumns} data={domainGridData} />
        </RightGrid>
    );
};

const MandatorySurveyWelcome: React.FC = () => {
    const logoImageSrc: string = useSelector(
        (state: State): string => state.surveyState.logoSrc || '',
    );
    return (
        <WelcomeSurveyContainer>
            <LogoImageContainer>
                <LogoImage src={`data:image/jpeg;base64,${logoImageSrc}`} />
            </LogoImageContainer>
            <BottomSectionContainer>
                <MandatorySurveyWelcomeLeft />
                <MandatorySurveyWelcomeRight />
            </BottomSectionContainer>
        </WelcomeSurveyContainer>
    );
};

export default MandatorySurveyWelcome;
